<template>
    <div class="sod-eod-history-list">
        <v-progress-circular v-if="loading" indeterminate color="primary" class="d-block mx-auto my-6" />

        <div v-else-if="!entries.length" class="text-body-2 grey--text text-center py-8">
            No Start of Day / End of Day submissions yet.
        </div>

        <v-expansion-panels v-else v-model="openPanels" multiple>
            <v-expansion-panel v-for="entry in entries" :key="entry.work_date" class="history-panel">
                <v-expansion-panel-header>
                    <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                        <span class="entry-date">{{ formatDate(entry.work_date) }}</span>
                        <v-chip v-if="entry.sod" small class="sod-chip" text-color="white">
                            <v-icon left small color="white">mdi-white-balance-sunny</v-icon>
                            SOD {{ formatTime(entry.sod.submitted_at) }}
                        </v-chip>
                        <v-chip v-else x-small outlined color="grey">No SOD</v-chip>
                        <v-chip v-if="entry.eod" small class="eod-chip" text-color="white">
                            <v-icon left small color="white">mdi-weather-night</v-icon>
                            EOD {{ formatTime(entry.eod.submitted_at) }}
                        </v-chip>
                        <v-chip v-else x-small outlined color="grey">No EOD</v-chip>
                    </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-row dense>
                        <v-col cols="12" md="6" class="entry-section">
                            <div class="inner-card inner-card-sod pa-3">
                                <div class="section-heading">
                                    <v-icon small color="amber darken-2" class="mr-1">mdi-white-balance-sunny</v-icon>
                                    Start of Day
                                </div>
                                <template v-if="entry.sod">
                                    <div class="text-caption grey--text mb-2 d-flex align-center">
                                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                                        Submitted {{ formatDateTime(entry.sod.submitted_at) }}
                                    </div>
                                    <div v-if="entry.sod.focus_note" class="text-body-2 mb-2">
                                        <strong>Focus:</strong> {{ entry.sod.focus_note }}
                                    </div>
                                    <div class="text-body-2 mb-2">
                                        Planned: {{ entry.sod.tasks ? entry.sod.tasks.length : (entry.sod.task_count || 0) }} task(s)
                                    </div>
                                    <ol v-if="entry.sod.tasks && entry.sod.tasks.length" class="task-numbered-list">
                                        <li v-for="(t, idx) in entry.sod.tasks" :key="t.task_id">
                                            <span class="task-number">{{ idx + 1 }}</span>
                                            <span>
                                                {{ t.name }}
                                                <span class="text-caption grey--text">({{ tierLabel(t.priority_tier) }})</span>
                                            </span>
                                        </li>
                                    </ol>
                                </template>
                                <div v-else class="text-body-2 grey--text">Not submitted</div>
                            </div>
                        </v-col>
                        <v-col cols="12" md="6" class="entry-section">
                            <div class="inner-card inner-card-eod pa-3">
                                <div class="section-heading">
                                    <v-icon small color="deep-purple" class="mr-1">mdi-weather-night</v-icon>
                                    End of Day
                                </div>
                                <template v-if="entry.eod">
                                    <div class="text-caption grey--text mb-2 d-flex align-center">
                                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                                        Submitted {{ formatDateTime(entry.eod.submitted_at) }}
                                    </div>
                                    <div class="text-body-2 font-weight-medium mb-2">
                                        Completed {{ entry.eod.completed_count ?? 0 }} of {{ entry.eod.planned_count ?? 0 }} planned
                                    </div>
                                    <ul v-if="entry.eod.tasks && entry.eod.tasks.length" class="task-check-list mb-2">
                                        <li v-for="t in entry.eod.tasks" :key="t.task_id" :class="{ 'task-completed': t.was_completed }">
                                            <v-icon x-small :color="t.was_completed ? 'success' : 'grey'" class="mr-1">
                                                {{ t.was_completed ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                            </v-icon>
                                            {{ t.name }}
                                        </li>
                                    </ul>
                                    <div v-if="entry.eod.biggest_accomplishment" class="text-body-2 mb-2">
                                        <strong>Accomplishment:</strong> {{ entry.eod.biggest_accomplishment }}
                                    </div>
                                    <div v-if="entry.eod.manager_note" class="text-body-2 mb-2">
                                        <strong>Manager note:</strong> {{ entry.eod.manager_note }}
                                    </div>
                                    <v-chip v-if="entry.eod.workload_pulse" x-small :color="pulseColor(entry.eod.workload_pulse)" text-color="white">
                                        Workload: {{ pulseLabel(entry.eod.workload_pulse) }}
                                    </v-chip>
                                </template>
                                <div v-else class="text-body-2 grey--text">Not submitted</div>
                            </div>
                        </v-col>
                    </v-row>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
export default {
    name: 'SodEodHistoryList',
    props: {
        entries: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            openPanels: [0],
        };
    },
    watch: {
        entries() {
            // Most-recent entry (index 0) starts expanded, matching the
            // approved layout — the rest stay collapsed but reachable.
            this.openPanels = this.entries.length ? [0] : [];
        },
    },
    methods: {
        formatDate(date) {
            if (!date) return '—';
            return this.$date(date).format('ddd, MMM D, YYYY');
        },
        formatTime(dateTime) {
            if (!dateTime) return '';
            return this.$date(dateTime).format(this.$tz.time);
        },
        formatDateTime(dateTime) {
            if (!dateTime) return '—';
            return this.$date(dateTime).format(`MMM D, YYYY ${this.$tz.time}`);
        },
        tierLabel(tier) {
            return { 1: 'Must Do', 2: 'Should Do', 3: 'If Time Allows' }[tier] || '—';
        },
        pulseLabel(pulse) {
            return { too_much: 'Too Much', about_right: 'About Right', comfortable: 'Comfortable' }[pulse] || pulse;
        },
        pulseColor(pulse) {
            return { too_much: 'red darken-1', about_right: 'amber darken-2', comfortable: 'green darken-1' }[pulse] || 'grey';
        },
    },
};
</script>

<style scoped>
.history-panel {
    border-radius: 12px !important;
    margin-bottom: 10px;
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.history-panel::before {
    box-shadow: none;
}

.entry-date {
    font-weight: 700;
    font-size: 1rem;
}

.sod-chip {
    background: linear-gradient(90deg, #f5b219, #f0980a) !important;
}

.eod-chip {
    background: linear-gradient(90deg, #8b5cf6, #6d43e0) !important;
}

.entry-section {
    padding: 8px;
}

.inner-card {
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    height: 100%;
}

.inner-card-sod {
    background: rgba(245, 178, 25, 0.05);
    border-left: 3px solid #f5b219;
}

.inner-card-eod {
    background: rgba(139, 92, 246, 0.05);
    border-left: 3px solid #8b5cf6;
}

.section-heading {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
}

.task-numbered-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.task-numbered-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    margin-bottom: 6px;
}

.task-number {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(47, 111, 237, 0.12);
    color: #2f6fed;
    font-size: 0.7rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.task-check-list {
    list-style: none;
    margin: 0;
    padding: 0;
}

.task-check-list li {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    margin-bottom: 4px;
}

.task-check-list li.task-completed {
    color: rgba(0, 0, 0, 0.6);
    text-decoration: line-through;
}
</style>
